import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  Edit3,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import useBulkOptionGroups from "../../services/Order/useBulkOptionGroups";
import useBulkPackages from "../../services/Order/useBulkPackages";
import useBulkCatalogue from "../../services/Order/useBulkCatalogue";

const EMPTY = {
  option_group_item_id: "",
  option_group_id: "",
  package_id: "",
  item_id: "",
  variant_id: "",
  quantity_per_person: "",
  fixed_quantity: "",
  extra_price: "",
  is_default: "0",
  display_order: "",
  status: "ACTIVE",
};
const field =
  "h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-emerald-400";

export default function BulkOptionGroupItems() {
  const {
    getOptionGroups,
    getOptionGroupItems,
    insertOptionGroupItem,
    updateOptionGroupItem,
    deleteOptionGroupItem,
    optionGroupItemsLoading,
    optionGroupItemSaving,
    optionGroupItemDeletingId,
  } = useBulkOptionGroups();
  const { getPackages } = useBulkPackages();
  const { getItems, getItemVariants } = useBulkCatalogue();
  const [rows, setRows] = useState([]),
    [groups, setGroups] = useState([]),
    [packages, setPackages] = useState([]),
    [items, setItems] = useState([]),
    [variants, setVariants] = useState([]),
    [form, setForm] = useState(EMPTY),
    [modal, setModal] = useState(false);

  const [itemSearch, setItemSearch] = useState("");

  const filteredItems = rows.filter((mapping) => {
    const normalize = (value) =>
      String(value ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const query = normalize(itemSearch.trim());

    if (!query) return true;

    const matchedGroup = groups.find(
      (g) =>
        String(g?.option_group_id ?? g?.id) ===
        String(mapping?.option_group_id),
    );

    const matchedPackage = packages.find(
      (p) => String(p?.package_id) === String(mapping?.package_id),
    );

    const matchedItem = items.find(
      (i) => String(i?.item_id ?? i?.id) === String(mapping?.item_id),
    );

    const matchedVariant = variants.find(
      (v) => String(v?.variant_id ?? v?.id) === String(mapping?.variant_id),
    );

    const isDefault = ["true", "1", "yes", "y"].includes(
      String(mapping?.is_default ?? "").toLowerCase(),
    );

    const searchableFields = [
      mapping?.option_group_item_id,
      mapping?.option_group_id,
      matchedGroup?.group_name,
      mapping?.package_id,
      matchedPackage?.package_name,
      mapping?.item_id,
      matchedItem?.item_name,
      mapping?.variant_id,
      matchedVariant?.variant_name,
      mapping?.quantity_per_person,
      mapping?.fixed_quantity,
      mapping?.extra_price,
      mapping?.display_order,
      mapping?.status,
      isDefault ? "Yes True 1" : "No False 0",
    ];

    return searchableFields.some((value) => normalize(value).includes(query));
  });

  const load = async () => {
    const r = await getOptionGroupItems("");
    setRows(Array.isArray(r) ? r : []);
  };
  const ensureOptions = async () => {
    const [g, p, i, v] = await Promise.all([
      groups.length ? Promise.resolve(groups) : getOptionGroups(""),
      packages.length ? Promise.resolve(packages) : getPackages(""),
      items.length ? Promise.resolve(items) : getItems("", ""),
      variants.length ? Promise.resolve(variants) : getItemVariants(""),
    ]);
    setGroups(Array.isArray(g) ? g : []);
    setPackages(Array.isArray(p) ? p : []);
    setItems(Array.isArray(i) ? i : []);
    setVariants(Array.isArray(v) ? v : []);
  };
useEffect(() => {
  load();
  ensureOptions();

  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const edit = async (r) => {
  await ensureOptions();
  setForm(
    Object.keys(EMPTY).reduce(
      (a, k) => ({ ...a, [k]: String(r?.[k] ?? EMPTY[k]) }),
      {},
    ),
  );
  setModal(true);
};
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      is_default:
        form.is_default === true ||
        form.is_default === 1 ||
        String(form.is_default).toLowerCase() === "true" ||
        String(form.is_default) === "1"
          ? 1
          : 0,
    };

    const saved = form.option_group_item_id
      ? await updateOptionGroupItem(payload)
      : await insertOptionGroupItem(payload);
    if (saved) {
      setModal(false);
      setForm(EMPTY);
      load();
    }
  };
  const name = (arr, idKey, nameKey, id, label) =>
    arr.find((x) => String(x?.[idKey] ?? x?.id) === String(id))?.[nameKey] ||
    `${label} #${id || "—"}`;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Option Group Items
          </h3>

          <p className="text-[11px] text-slate-500">
            Manage items and variants inside option groups.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* OPTION GROUP ITEM SEARCH */}
          <div className="relative w-full sm:w-64 lg:w-80">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Search item, package, group, ID..."
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-xs text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />

            {itemSearch && (
              <button
                type="button"
                onClick={() => setItemSearch("")}
                className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={load}
            disabled={optionGroupItemsLoading}
            className="inline-flex h-8 items-center gap-1 rounded-lg border px-3 text-xs"
          >
            <RefreshCw
              size={13}
              className={optionGroupItemsLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
          <button
            type="button"
            onClick={async () => {
              await ensureOptions();
              setForm(EMPTY);
              setModal(true);
            }}
            className="inline-flex h-8 items-center gap-1 rounded-lg bg-emerald-600 px-3 text-xs font-semibold text-white"
          >
            <Plus size={13} />
            Add Item
          </button>
        </div>
      </div>
      {optionGroupItemsLoading ? (
        <div className="h-24 animate-pulse rounded-xl border bg-white" />
      ) : !filteredItems.length ? (
        <div className="rounded-xl border border-dashed bg-white p-12 text-center text-sm text-slate-500">
          {itemSearch.trim()
            ? "No option group items match your search."
            : "No option group items found."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {filteredItems.map((r, i) => {
            const id = String(r?.option_group_item_id ?? "");
            return (
              <div
                key={id || i}
                className="flex items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <b className="text-sm">
                    {name(items, "item_id", "item_name", r?.item_id, "Item")}
                  </b>
                  <p className="text-[11px] text-slate-500">
                    ID #{id || "—"} ·{" "}
                    {name(
                      groups,
                      "option_group_id",
                      "group_name",
                      r?.option_group_id,
                      "Group",
                    )}{" "}
                    ·{" "}
                    {name(
                      packages,
                      "package_id",
                      "package_name",
                      r?.package_id,
                      "Package",
                    )}{" "}
                    · Extra ₹{r?.extra_price || "0"} · Default{" "}
                    {r?.is_default || "0"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => edit(r)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  type="button"
                  disabled={optionGroupItemDeletingId === id}
                  onClick={async () => {
                    if (await deleteOptionGroupItem(id)) load();
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600"
                >
                  {optionGroupItemDeletingId === id ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <Trash2 size={13} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
      {modal && (
        <div className="fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/45 p-3">
          <div className="relative w-full max-w-4xl overflow-visible rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="font-semibold">
                {form.option_group_item_id
                  ? "Edit Option Group Item"
                  : "Add Option Group Item"}
              </h3>
              <button type="button" onClick={() => setModal(false)}>
                <X size={17} />
              </button>
            </div>
            <form onSubmit={submit}>
              <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
                <SearchableDropdown
                  label="Option Group"
                  options={groups}
                  idKey="option_group_id"
                  nameKey="group_name"
                  value={form.option_group_id}
                  disabled={optionGroupItemSaving}
                  onChange={(id) =>
                    setForm((prev) => ({
                      ...prev,
                      option_group_id: id,
                    }))
                  }
                />

                <SearchableDropdown
                  label="Package"
                  options={packages}
                  idKey="package_id"
                  nameKey="package_name"
                  value={form.package_id}
                  disabled={optionGroupItemSaving}
                  onChange={(id) =>
                    setForm((prev) => ({
                      ...prev,
                      package_id: id,
                    }))
                  }
                />

                <SearchableDropdown
                  label="Item"
                  options={items}
                  idKey="item_id"
                  nameKey="item_name"
                  value={form.item_id}
                  disabled={optionGroupItemSaving}
                  onChange={(id) =>
                    setForm((prev) => ({
                      ...prev,
                      item_id: id,
                      variant_id: "",
                    }))
                  }
                />

                <Select
                  label="Variant"
                  value={form.variant_id}
                  set={(v) => setForm((p) => ({ ...p, variant_id: v }))}
                >
                  <option value="">Select Variant</option>
                  {variants
                    .filter((v) => String(v?.item_id) === String(form.item_id))
                    .map((v) => (
                      <option key={v?.variant_id} value={v?.variant_id}>
                        {v?.variant_name || `Variant #${v?.variant_id}`} — ID:{" "}
                        {v?.variant_id}
                      </option>
                    ))}
                </Select>
                {[
                  "quantity_per_person",
                  "fixed_quantity",
                  "extra_price",
                  "display_order",
                ].map((k) => (
                  <Input
                    key={k}
                    label={k
                      .split("_")
                      .map((x) => x[0].toUpperCase() + x.slice(1))
                      .join(" ")}
                    name={k}
                    form={form}
                    setForm={setForm}
                  />
                ))}
                <Select
                  label="Is Default"
                  value={form.is_default}
                  set={(v) => setForm((p) => ({ ...p, is_default: v }))}
                >
                  <option value="1">1</option>
                  <option value="0">0</option>
                </Select>
                <Select
                  label="Status"
                  value={form.status}
                  set={(v) => setForm((p) => ({ ...p, status: v }))}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </Select>
              </div>
              <div className="flex justify-end gap-2 border-t bg-slate-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setModal(false)}
                  className="h-9 rounded-lg border bg-white px-4 text-xs font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={optionGroupItemSaving}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-4 text-xs font-semibold text-white"
                >
                  {optionGroupItemSaving ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : form.option_group_item_id ? (
                    <Edit3 size={13} />
                  ) : (
                    <Plus size={13} />
                  )}{" "}
                  {optionGroupItemSaving
                    ? "Saving..."
                    : form.option_group_item_id
                      ? "Update"
                      : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}


function SearchableDropdown({
  label,
  options = [],
  idKey,
  nameKey,
  value,
  onChange,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const getId = (row) => String(row?.[idKey] ?? row?.id ?? "");

  const getName = (row) => String(row?.[nameKey] || `${label} #${getId(row)}`);

  const selected = options.find((row) => getId(row) === String(value));

  const filtered = options.filter((row) => {
    const query = search.trim().toLowerCase();

    return (
      getName(row).toLowerCase().includes(query) || getId(row).includes(query)
    );
  });

  useEffect(() => {
    if (!open || disabled) return;

    searchRef.current?.focus();

    const handleOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open, disabled]);

  const selectOption = (row) => {
    onChange(getId(row));
    setOpen(false);
    setSearch("");
  };

  const clearSelection = () => {
    onChange("");
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={dropdownRef} className={`relative min-w-0 ${open ? "z-50" : ""}`}>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>

      {/* SELECT FIELD */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((prev) => !prev);
          setSearch("");
        }}
        aria-haspopup="listbox"
        aria-expanded={open && !disabled}
        className={`flex h-10 w-full items-center justify-between gap-2 rounded-xl border bg-white px-3 text-left text-sm shadow-sm transition outline-none disabled:cursor-not-allowed disabled:opacity-60 ${
          open
            ? "border-emerald-400 ring-2 ring-emerald-100"
            : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <span
          className={`truncate ${
            selected ? "text-slate-800" : "text-slate-400"
          }`}
        >
          {selected
            ? getName(selected)
            : value
              ? `${label} #${value}`
              : `Select ${label}`}
        </span>

        <ChevronDown
          size={16}
          className={`shrink-0 text-slate-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* DROPDOWN */}
      {open && !disabled && (
        <div className="absolute left-0 right-0 top-full z-[100] mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.12)]">
          {/* SEARCH BAR */}
          <div className="border-b border-slate-100 p-2">
            <div className="relative">
              <Search
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                ref={searchRef}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();

                    if (filtered.length) {
                      selectOption(filtered[0]);
                    }
                  }

                  if (e.key === "Escape") {
                    setOpen(false);
                    setSearch("");
                  }
                }}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none"
              />
            </div>
          </div>

          {/* MAX 6 ROWS — SCROLLBAR HIDDEN */}
          <div
            role="listbox"
            aria-label={label}
            className="max-h-[216px] overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {!search.trim() && (
              <button
                type="button"
                role="option"
                aria-selected={!value}
                onClick={clearSelection}
                className={`flex h-9 w-full items-center px-3 text-left text-sm transition ${
                  !value
                    ? "bg-emerald-50 font-medium text-emerald-700"
                    : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                Select {label}
              </button>
            )}

            {filtered.length > 0 ? (
              filtered.map((row) => {
                const id = getId(row);
                const active = String(value) === id;

                return (
                  <button
                    key={id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => selectOption(row)}
                    className={`flex h-9 w-full items-center px-3 text-left text-sm transition ${
                      active
                        ? "bg-emerald-50 font-medium text-emerald-700"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span className="truncate">{getName(row)}</span>
                  </button>
                );
              })
            ) : (
              <p className="px-3 py-4 text-center text-sm text-slate-400">
                No {label.toLowerCase()}s found
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function Select({ label, value, set, children }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => set(e.target.value)}
        className={field}
      >
        {children}
      </select>
    </label>
  );
}
function Input({ label, name, form, setForm }) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-semibold text-slate-700">
        {label}
      </span>
      <input
        type="number"
        value={form[name]}
        onChange={(e) => setForm((p) => ({ ...p, [name]: e.target.value }))}
        className={field}
      />
    </label>
  );
}
